import { Fragment } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Radios from './Radios';
import { Changed, changedRing } from './Changed';
import { usd, fmtNum, inputCls } from '../../lib/q2c';
import { countWord } from '../../lib/countWord.mjs';

const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
// Costs in whole dollars (the detail keeps the cents).
const dollars = (n) => (n == null ? '—' : usd(Math.round(Number(n)), 0));
const FLAG = {
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  none: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-300',
};
const isTools = (l) => l.type === 'tools' || l.id === 'tools';

function Flag({ level, children }) {
  return <span className={`inline-block whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ${FLAG[level] || FLAG.none}`}>{children}</span>;
}

/** A cost cell: the amount, where it came from (its tag), and a warning when it is above our sell price. */
function CostCell({ c, sell }) {
  if (!c) return <span className="text-xs text-[var(--text-secondary)]">—</span>;
  const wait = c.kind === 'waiting';
  return (
    <div className="grid justify-items-end gap-0.5">
      {c.amount != null && <span className="tabular-nums">{dollars(c.amount)}</span>}
      <span title={c.detail || undefined} className={`whitespace-nowrap rounded border px-1.5 text-[11px] ${wait ? 'border-amber-500 text-amber-700 dark:text-amber-300' : 'border-[var(--border)] text-[var(--text-secondary)]'}`}>{c.tag}</span>
      {c.amount != null && sell != null && Number(sell) < c.amount && <span className="text-[11px] font-medium text-red-700 dark:text-red-300">Above our sell price</span>}
    </div>
  );
}

// A margin under cost is red; the margin the check used, when the line is below the floor, amber.
function marginClass(row, k) {
  const m = row.margin[k];
  if (m == null) return '';
  if (m < 0) return 'font-medium text-red-700 dark:text-red-300';
  return row.check.on === k && row.check.level === 'warn' ? 'font-medium text-amber-700 dark:text-amber-300' : '';
}

function qtyText(l, r) {
  if (!r) return '—';
  if (r.pricing === 'per_sf') return <><p className="font-medium">{fmtNum(r.area_sf)} sf</p>{r.bags != null && <p className="text-xs text-[var(--text-secondary)]">{fmtNum(r.bags)} {countWord(r.bags, 'bag')}</p>}</>;
  if (r.pricing === 'lump') return <p>lump sum</p>;
  return <p className="font-medium">{fmtNum(r.qty, 0, 4)} {countWord(r.qty, r.unit)}</p>;
}

function sellUnit(l) {
  if (l.pricing === 'per_sf') return '/ sf';
  if (l.pricing === 'lump') return 'lump';
  return `/ ${l.unit || 'unit'}`;
}

/**
 * 2. Lines, cost and price (LP-402): quantity, expected cost, true cost, sell, margin and a check per line.
 * Each cost says where it came from. Below cost is red; below the floor (cost + 40% markup, freight at
 * cost) amber. The sell price is typed here; "Details" opens the full line editor.
 *
 * lines / off: the form's lines and the lines left off; rows: the builder view's lines by id; totals:
 * the view's totals. tagOf(path): the Copilot change that tagged a field.
 */
export default function LinesTable({ lines, off = [], rows, totals, problems = [], basis, setBasis, offerRef, floorRule, editable, setPrice, tools, setTools, tagOf, openId, setOpenId, renderDetails, pending, error }) {
  const lineTag = (id, k) => tagOf(`line:${id}.${k}`);
  const shown = [...lines.map((l) => ({ l, off: false })), ...off.filter(isTools).map((l) => ({ l, off: true }))];
  const anyBad = problems.some((p) => p.level === 'bad');
  return (
    <section className="space-y-3 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">2. Lines, cost and price</h2>
          <p className="text-xs text-[var(--text-secondary)]">Each cost says where it came from. {floorRule}</p>
          {pending && <p className="text-xs text-[var(--text-secondary)]">Updating the numbers…</p>}
          {error && <p className="text-xs text-red-600">The numbers could not be updated: {error}</p>}
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">Check margins against</p>
          <Radios name="qb-basis" value={basis} onChange={setBasis}
            options={[
              { value: 'true', label: 'True cost', hint: offerRef ? `Concria's written price (offer ${offerRef})` : "Concria's RFQ answer (waiting on RFQ: expected cost is used until then)" },
              { value: 'expected', label: 'Expected cost', hint: 'Catalog cost versions and the freight estimate' },
            ]} />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="w-full min-w-[47rem] text-sm [&_td]:px-1.5 [&_th]:px-1.5">
          <thead className="bg-gray-50 text-left text-xs dark:bg-slate-800">
            <tr>
              <th className="p-2">Item</th><th className="p-2">Quantity</th><th className="p-2 text-right">Expected cost</th><th className="p-2 text-right">True cost</th>
              <th className="p-2">Sell</th><th className="p-2 text-right">Margin</th><th className="p-2">Check</th>
            </tr>
          </thead>
          <tbody className="align-top">
            {shown.map(({ l, off: isOff }, i) => {
              const r = rows.get(l.id);
              const open = openId === l.id;
              const rowTag = tagOf(`line:${l.id}`) || lineTag(l.id, 'qty') || lineTag(l.id, 'area_sf');
              return (
                <Fragment key={l.id}>
                  <tr key={`r${rowTag || ''}`} className={`border-t border-[var(--border)] ${isOff || l.optional ? 'text-[var(--text-secondary)]' : ''} ${rowTag ? 'qb-flash-row' : ''}`}>
                    <td className="p-2">
                      <p className="font-medium text-[var(--text-primary)]">{l.description || '(no description)'}</p>
                      {r?.sku && <p className="text-xs text-[var(--text-secondary)]">{r.sku}</p>}
                      {r?.tariff && <p className="mt-0.5 text-[11px] text-[var(--text-secondary)]">{r.tariff.tag}</p>}
                      {isTools(l) && (
                        <div className="mt-2">
                          <Radios name={`qb-tools-${l.id}`} disabled={!editable} value={tools} onChange={setTools}
                            options={[{ value: 'optional', label: 'Optional, not in total' }, { value: 'included', label: 'In the total' }, { value: 'dropped', label: 'Leave off the quote' }]} />
                          <Changed id={tagOf(`line:${l.id}`) || lineTag(l.id, 'optional')} key={`tt${tagOf(`line:${l.id}`) || lineTag(l.id, 'optional') || ''}`} />
                        </div>
                      )}
                      {!isOff && renderDetails && (
                        <button type="button" className="mt-1 inline-flex items-center gap-0.5 text-xs text-primary" onClick={() => setOpenId(open ? null : l.id)} aria-expanded={open}>
                          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Details
                        </button>
                      )}
                    </td>
                    <td className="p-2 tabular-nums">{isOff ? '—' : <>{qtyText(l, r)}<Changed id={rowTag} key={`q${rowTag || ''}`} /></>}</td>
                    <td className="p-2 text-right">{isOff ? '' : <CostCell c={r?.expected} sell={r?.sell.amount} />}</td>
                    <td className="p-2 text-right">{isOff ? '' : <CostCell c={r?.true} sell={r?.sell.amount} />}</td>
                    <td className="p-2">
                      {isOff ? <span className="text-xs">Not on the quote</span> : (
                        <div className="grid justify-items-start gap-1">
                          <span className="flex items-center gap-1">
                            <input className={`${inputCls} w-28 !px-2 text-right tabular-nums ${changedRing(lineTag(l.id, 'unit_price'))}`} inputMode="decimal" disabled={!editable}
                              aria-label={`Sell price, ${l.description}`} value={l.unit_price} onChange={(e) => setPrice(l.id, e.target.value)} />
                            <span className="whitespace-nowrap text-xs text-[var(--text-secondary)]">{sellUnit(l)}</span>
                          </span>
                          <span className="font-semibold tabular-nums text-[var(--text-primary)]">{r ? usd(r.sell.amount) : '—'}</span>
                          {l.optional && <span className="text-xs">Not in total</span>}
                          <Changed id={lineTag(l.id, 'unit_price')} key={`p${lineTag(l.id, 'unit_price') || ''}`} />
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {isOff || !r ? '' : (
                        <div className="grid justify-items-end gap-0.5 text-xs">
                          <span className={`whitespace-nowrap ${marginClass(r, 'expected')}`}>Exp {pct(r.margin.expected)}</span>
                          <span className={`whitespace-nowrap ${marginClass(r, 'true')}`}>True {pct(r.margin.true)}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      {isOff || !r ? '' : (
                        <div className="grid justify-items-start gap-0.5">
                          <Flag level={r.check.level}>{r.check.text}</Flag>
                          {r.check.on && r.check.on !== basis && <span className="text-[11px] text-[var(--text-secondary)]">on {r.check.on} cost</span>}
                          {r.check.floor_unit != null && r.type !== 'freight' && <span className="whitespace-nowrap text-[11px] text-[var(--text-secondary)]">floor {usd(Math.ceil(Number((r.check.floor_unit * 100).toFixed(6))) / 100)}{r.pricing === 'per_sf' ? '/sf' : ''}</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                  {open && !isOff && (
                    <tr className="border-t border-dashed border-[var(--border)]"><td colSpan={7} className="bg-gray-50 p-2 dark:bg-slate-800/40">{renderDetails(l, i)}</td></tr>
                  )}
                </Fragment>
              );
            })}
            {!shown.length && <tr><td colSpan={7} className="p-3 text-sm text-[var(--text-secondary)]">No lines yet.</td></tr>}
          </tbody>
          {totals && (
            <tfoot className="bg-gray-50 font-medium dark:bg-slate-800">
              <tr className="border-t border-[var(--border)] align-top">
                <td className="p-2">Total</td>
                <td className="p-2" />
                <td className="p-2 text-right tabular-nums">{dollars(totals.expected.cost)}{totals.expected.lines_without.length > 0 && <p className="text-[11px] font-normal text-[var(--text-secondary)]">{totals.expected.lines_without.length} line{totals.expected.lines_without.length === 1 ? '' : 's'} without</p>}</td>
                <td className="p-2 text-right tabular-nums">{totals.true.cost != null ? dollars(totals.true.cost) : <span className="text-xs font-normal text-amber-700 dark:text-amber-300">Waiting on RFQ</span>}{totals.true.cost != null && totals.true.lines_without.length > 0 && <p className="text-[11px] font-normal text-[var(--text-secondary)]">{totals.true.lines_without.length} still waiting</p>}</td>
                <td className="p-2 tabular-nums"><p>{usd(totals.total)}</p>{totals.per_sf != null && <p className="text-xs font-normal text-[var(--text-secondary)]">{usd(totals.per_sf, 2)} per sf{tools === 'optional' ? ', tools optional' : ''}</p>}</td>
                <td className="whitespace-nowrap p-2 text-right text-xs tabular-nums"><p>Exp {pct(totals.expected.margin_pct)}</p><p>True {pct(totals.true.margin_pct)}</p></td>
                <td className="p-2">{problems.length ? <Flag level={anyBad ? 'bad' : 'warn'}>{problems.length} line{problems.length === 1 ? '' : 's'} to fix</Flag> : <Flag level="ok">All lines OK</Flag>}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}
